import { RepairTaskLibrary } from "./RepairTaskLibrary";
import { RepairDiscountFields, RepairPriceBreakdown } from "./RepairPricing";
import { RepairNextStep } from "./RepairNextStep";
import { DealSupportingDocuments } from "./DealSupportingDocuments";
import { DealProposalPreview } from "./DealProposalPreview";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Alert, Box, Button, Card, Chip, Stack, Typography } from "@mui/joy";
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  UploadSimple,
  Images,
  CheckCircle,
  ClipboardText,
  FileText,
  Plus,
  ShieldCheck,
  Wrench,
} from "@phosphor-icons/react";
import { EvidenceImage } from "./EvidenceImage";
import { DealOpportunityPhotos } from "./DealOpportunityPhotos";
import { downloadApiBlob } from "../../lib/api";
import {
  proposalDealsApi,
  dealError,
  type Deal,
  type DealDocument,
  type Finding,
  type RepairItem,
} from "../../lib/proposalDealsApi";
import "./dealProposals.css";

type Values = Record<string, string>;
const currency = (value: string) =>
  Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });

function SavedForm({
  cacheKey,
  revision,
  onSave,
  children,
  submit = "Save changes",
  disabled = false,
}: {
  cacheKey: string;
  revision: number;
  onSave: (values: Values, revision: number) => Promise<void>;
  children: ReactNode;
  submit?: string;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLFormElement>(null);
  const base = useRef(revision);
  const [serverValues, setServerValues] = useState<Values>({});
  const [baseVersion, setBaseVersion] = useState(revision);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [recovered, setRecovered] = useState(false);
  useEffect(() => {
    try {
      setServerValues(
        Object.fromEntries(new FormData(ref.current!).entries()) as Values,
      );
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as { revision: number; values: Values };
      Object.entries(saved.values).forEach(([name, value]) => {
        const element = ref.current?.elements.namedItem(name);
        if (
          element instanceof HTMLInputElement ||
          element instanceof HTMLTextAreaElement ||
          element instanceof HTMLSelectElement
        )
          element.value = value;
      });
      if (
        saved.values.exception_reason ||
        ["not_applicable", "inaccessible"].includes(saved.values.result)
      ) {
        const details =
          ref.current?.querySelector<HTMLDetailsElement>(".deal-exception");
        if (details) details.open = true;
      }
      base.current = saved.revision;
      setBaseVersion(saved.revision);
      setRecovered(true);
    } catch {
      /* Browser storage may be disabled. Server saves remain available. */
    }
  }, [cacheKey]);
  const values = () =>
    Object.fromEntries(new FormData(ref.current!).entries()) as Values;
  return (
    <form
      ref={ref}
      className="deal-form"
      onChange={() => {
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ revision: base.current, values: values() }),
          );
        } catch {
          /* Device storage may be unavailable; server edits remain usable. */
        }
      }}
      onSubmit={async (e) => {
        e.preventDefault();
        setPending(true);
        setError("");
        const submitted = values();
        try {
          try {
            localStorage.removeItem(cacheKey);
          } catch {
            /* Device storage may be unavailable; server edits remain usable. */
          }
          await onSave(submitted, base.current);
          setRecovered(false);
        } catch (failure) {
          try {
            localStorage.setItem(
              cacheKey,
              JSON.stringify({ revision: base.current, values: submitted }),
            );
          } catch {
            /* Device storage may be unavailable; server edits remain usable. */
          }
          setError(dealError(failure));
        } finally {
          setPending(false);
        }
      }}
    >
      {recovered && (
        <Alert
          color="warning"
          endDecorator={
            <Button
              size="sm"
              variant="plain"
              onClick={() => {
                localStorage.removeItem(cacheKey);
                ref.current?.reset();
                base.current = revision;
                setBaseVersion(revision);
                setRecovered(false);
              }}
            >
              Discard draft
            </Button>
          }
        >
          Recovered unsaved changes
          {baseVersion !== revision
            ? " from an older version. Review before saving."
            : "."}
        </Alert>
      )}
      {recovered && baseVersion !== revision && (
        <Alert color="warning">
          <div>
            <Typography level="title-sm">Current saved values</Typography>
            {Object.entries(serverValues).map(([key, value]) => (
              <Typography key={key} level="body-sm">
                {key.replaceAll("_", " ")}: {value || "Empty"}
              </Typography>
            ))}
            <Button
              size="sm"
              onClick={() => {
                base.current = revision;
                setBaseVersion(revision);
                try {
                  localStorage.setItem(
                    cacheKey,
                    JSON.stringify({ revision, values: values() }),
                  );
                } catch {
                  /* Device storage may be unavailable; server edits remain usable. */
                }
              }}
            >
              Keep my draft on the current version
            </Button>
          </div>
        </Alert>
      )}
      <fieldset disabled={disabled || pending}>
        {children}
        <div className="deal-form-footer">
          <Typography level="body-xs">
            {disabled
              ? "This scope has been issued."
              : "Save to update the shared workspace."}
          </Typography>
          <Button
            type="submit"
            loading={pending}
            disabled={disabled || baseVersion !== revision}
          >
            {submit}
          </Button>
        </div>
      </fieldset>
      {error && <Alert color="danger">{error}</Alert>}
    </form>
  );
}
function Field({
  label,
  name,
  value = "",
  type = "text",
  required = false,
}: {
  label: string;
  name: string;
  value?: string | null;
  type?: string;
  required?: boolean;
}) {
  return (
    <label>
      {label}
      <input
        name={name}
        type={type}
        defaultValue={value || ""}
        required={required}
        step={type === "number" ? "0.001" : undefined}
      />
    </label>
  );
}
function Notes({
  label,
  name,
  value = "",
  required = false,
}: {
  label: string;
  name: string;
  value?: string;
  required?: boolean;
}) {
  return (
    <label>
      {label}
      <textarea name={name} defaultValue={value} rows={3} required={required} />
    </label>
  );
}

function EvidenceUpload({
  deal,
  finding,
  item,
  refresh,
}: {
  deal: Deal;
  finding?: number;
  item?: number;
  refresh: () => Promise<void>;
}) {
  const [pending, setPending] = useState(false),
    [error, setError] = useState("");
  const [picking, setPicking] = useState(false);
  const [fileName, setFileName] = useState("");
  const uploadId = useRef<string>(crypto.randomUUID());
  const selected = useRef<File | null>(null);
  const photos = deal.evidence.filter((e) =>
    finding ? e.finding === finding : e.repair_item === item,
  );
  return (
    <section className="deal-evidence" aria-label="Photo evidence">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Typography level="title-sm" startDecorator={<Camera size={18} />}>
          Photo evidence
        </Typography>
        <Chip size="sm" variant="soft" color="success">
          {photos.length} added
        </Chip>
      </Stack>
      <div className="deal-photos">
        {photos.map((p) => (
          <figure key={p.id}>
            <a
              href={p.image_url}
              target="_blank"
              rel="noreferrer"
              aria-label="Open original photo"
              title="Open original photo"
            >
              <EvidenceImage photo={p} />
            </a>
            <figcaption>{p.caption || "Inspection photo"}</figcaption>
          </figure>
        ))}
      </div>
      {picking && (
        <DealOpportunityPhotos
          deal={deal}
          finding={finding}
          item={item}
          refresh={refresh}
          onClose={() => setPicking(false)}
        />
      )}
      {!deal.scope_locked && (
        <form
          className="deal-form"
          onSubmit={async (e) => {
            e.preventDefault();
            setError("");
            setPending(true);
            const form = e.currentTarget;
            const data = new FormData(form);
            const file = data.get("image") as File;
            if (
              selected.current !== file &&
              (selected.current?.name !== file.name ||
                selected.current?.lastModified !== file.lastModified)
            ) {
              uploadId.current = crypto.randomUUID();
              selected.current = file;
            }
            data.set("upload_id", uploadId.current);
            data.set(
              finding ? "finding" : "repair_item",
              String(finding || item),
            );
            try {
              await proposalDealsApi.action(deal.id, "evidence", data);
              form.reset();
              selected.current = null;
              setFileName("");
              uploadId.current = crypto.randomUUID();
              await refresh();
            } catch (failure) {
              setError(dealError(failure));
            } finally {
              setPending(false);
            }
          }}
        >
          <div className="deal-evidence-actions">
            <Button
              component="label"
              variant="soft"
              startDecorator={<UploadSimple size={18} />}
              disabled={pending}
            >
              Add photo
              <input
                type="file"
                name="image"
                accept="image/jpeg,image/png,image/webp"
                aria-label="Add photo from your device"
                hidden
                disabled={pending}
                onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
              />
            </Button>
            <Button
              variant="outlined"
              startDecorator={<Images size={18} />}
              disabled={pending}
              onClick={() => setPicking(true)}
            >
              Choose from Opportunity
            </Button>
          </div>
          {fileName ? (
            <div className="deal-selected-photo">
              <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>
                {fileName}
              </Typography>
              <Field label="Caption (optional)" name="caption" />
              <Button
                type="submit"
                loading={pending}
                startDecorator={<UploadSimple size={18} />}
              >
                Upload evidence
              </Button>
            </div>
          ) : (
            <Typography level="body-xs">
              JPEG, PNG or WebP · Up to 20 MB per photo
            </Typography>
          )}
        </form>
      )}
      {error && <Alert color="danger">{error}</Alert>}
    </section>
  );
}

function FindingEditor({
  deal,
  finding,
  scope,
  refresh,
}: {
  deal: Deal;
  finding: Finding;
  scope: string;
  refresh: () => Promise<void>;
}) {
  const count = deal.evidence.filter((e) => e.finding === finding.id).length;
  return (
    <Card className="deal-editor">
      <Stack
        direction="row"
        justifyContent="space-between"
        gap={1}
        useFlexGap
        flexWrap="wrap"
      >
        <Typography level="h4" component="h2">
          {finding.title}
        </Typography>
        <Chip
          size="sm"
          variant="soft"
          color={count >= finding.minimum_photos ? "success" : "neutral"}
          startDecorator={<Camera size={14} />}
        >
          {count >= finding.minimum_photos
            ? "Photo requirement met"
            : `${count} of ${finding.minimum_photos} photos`}
        </Chip>
      </Stack>
      <Typography level="body-sm">
        Record what you observed, then attach the supporting photos.
      </Typography>
      <div className="deal-finding-body">
        <SavedForm
          key={deal.revision}
          cacheKey={`${scope}:finding:${finding.id}`}
          revision={deal.revision}
          onSave={async (v, revision) => {
            await proposalDealsApi.patch(
              deal.id,
              {
                ...v,
                revision,
                approve_exception: v.approve_exception === "yes",
              },
              `findings/${finding.id}/`,
            );
            await refresh();
          }}
        >
          <Field
            label="Location / roof plane"
            name="location"
            value={finding.location}
          />
          <Notes label="Observed findings" name="notes" value={finding.notes} />
          <label>
            Result
            <select
              name="result"
              defaultValue={finding.result}
              onChange={(e) => {
                if (
                  ["not_applicable", "inaccessible"].includes(e.target.value)
                ) {
                  const details = e.currentTarget
                    .closest("fieldset")
                    ?.querySelector("details");
                  if (details) details.open = true;
                }
              }}
            >
              <option value="pending">In progress</option>
              <option value="documented">Documented</option>
              {finding.allow_exception && (
                <>
                  <option value="not_applicable">Not applicable</option>
                  <option value="inaccessible">Unsafe / inaccessible</option>
                </>
              )}
            </select>
          </label>
          {finding.allow_exception && (
            <details
              className="deal-exception"
              open={
                !!finding.exception_reason ||
                ["not_applicable", "inaccessible"].includes(finding.result)
              }
            >
              <summary>Unable to document this area?</summary>
              <Typography level="body-sm">
                Choose an exception result above, explain why, and record a
                staff review.
              </Typography>
              <Notes
                label="Exception reason (required for exceptions)"
                name="exception_reason"
                value={finding.exception_reason}
              />
              <label>
                Exception review
                <select name="approve_exception" defaultValue="no">
                  <option value="no">Not reviewed / leave pending</option>
                  <option value="yes">
                    I reviewed and approve this documented exception
                  </option>
                </select>
              </label>
            </details>
          )}
        </SavedForm>
        <EvidenceUpload deal={deal} finding={finding.id} refresh={refresh} />
      </div>
    </Card>
  );
}
function RepairEditor({
  deal,
  item,
  scope,
  refresh,
}: {
  deal: Deal;
  item?: RepairItem;
  scope: string;
  refresh: () => Promise<void>;
}) {
  const [removeError, setRemoveError] = useState("");
  const [removing, setRemoving] = useState(false);
  return (
    <Card className="deal-editor" variant="plain">
      <Typography level="title-md">
        {item ? "Edit scope item" : "New scope item"}
      </Typography>
      <SavedForm
        key={deal.revision}
        cacheKey={`${scope}:repair:${item?.id || "new"}`}
        revision={deal.revision}
        disabled={deal.scope_locked}
        submit={item ? "Save scope item" : "Add scope item"}
        onSave={async (v, revision) => {
          if (item)
            await proposalDealsApi.patch(
              deal.id,
              { ...v, revision },
              `repair-items/${item.id}/`,
            );
          else
            await proposalDealsApi.action(deal.id, "repair-items", {
              ...v,
              revision,
            });
          await refresh();
        }}
      >
        <Field label="Title" name="title" value={item?.title} required />
        <Field
          label="Location"
          name="location"
          value={item?.location}
          required
        />
        <Notes label="Issue" name="issue" value={item?.issue} />
        <Field label="System" name="system" value={item?.system} />
        <Notes
          label="Solution / included work"
          name="solution"
          value={item?.solution}
          required
        />
        <div className="deal-fields">
          <Field
            label="Quantity"
            name="quantity"
            value={item?.quantity || "1"}
            type="number"
            required
          />
          <Field
            label="Unit"
            name="unit"
            value={item?.unit || "item"}
            required
          />
          <Field
            label="Unit price ($)"
            name="unit_price"
            value={item?.unit_price || "0.00"}
            type="number"
            required
          />
        </div>
        <RepairDiscountFields
          type={item?.discount_type}
          value={item?.discount_value}
          label="Line item discount"
        />
      </SavedForm>
      {removeError && <Alert color="danger">{removeError}</Alert>}
      {item && !deal.scope_locked && (
        <Button
          variant="plain"
          color="danger"
          loading={removing}
          onClick={async () => {
            if (!window.confirm(`Remove “${item.title}” from this draft?`))
              return;
            setRemoving(true);
            try {
              await proposalDealsApi.removeItem(
                deal.id,
                item.id,
                deal.revision,
              );
              await refresh();
            } catch (e) {
              setRemoveError(dealError(e));
            } finally {
              setRemoving(false);
            }
          }}
        >
          Remove scope item
        </Button>
      )}
      {item && (
        <>
          <EvidenceUpload deal={deal} item={item.id} refresh={refresh} />
          {item.source_item && (
            <Typography level="body-sm">
              Imported from Maintenance finding #{item.source_item}
            </Typography>
          )}
        </>
      )}
    </Card>
  );
}

function MaintenanceImport({
  deal,
  refresh,
}: {
  deal: Deal;
  refresh: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false),
    [selected, setSelected] = useState<number[]>([]),
    [error, setError] = useState(""),
    [pending, setPending] = useState(false);
  const query = useQuery({
    queryKey: ["proposal-maintenance-findings", deal.id],
    enabled: open,
    queryFn: () =>
      import("../../lib/api").then(({ apiClient }) =>
        apiClient
          .get<
            {
              id: number;
              title: string;
              location: string;
              property: string;
              estimated_cost: string | null;
              eligible: boolean;
            }[]
          >(`/api/proposal-deals/${deal.id}/maintenance-findings/`)
          .then((r) => r.data),
      ),
  });
  return (
    <Card className="deal-maintenance">
      <Typography level="title-md">
        Already inspected in Maintenance?
      </Typography>
      <Typography level="body-sm">
        Bring existing findings into this repair scope.
      </Typography>
      <Button
        variant="outlined"
        aria-expanded={open}
        disabled={deal.scope_locked}
        onClick={() => setOpen(!open)}
      >
        Import Maintenance findings
      </Button>
      {open && (
        <>
          <Typography level="body-sm">
            Select unresolved findings for this homeowner and property. Existing
            billing estimates remain unchanged.
          </Typography>
          {query.isPending && (
            <Typography level="body-sm">Loading findings…</Typography>
          )}
          {query.isError && (
            <Alert color="danger">Unable to load Maintenance findings.</Alert>
          )}
          {query.data?.map((item) => (
            <label className="deal-check" key={item.id}>
              <input
                type="checkbox"
                disabled={!item.eligible}
                checked={selected.includes(item.id)}
                onChange={(e) =>
                  setSelected(
                    e.target.checked
                      ? [...selected, item.id]
                      : selected.filter((id) => id !== item.id),
                  )
                }
              />
              <span>
                {item.title} ·{" "}
                {item.estimated_cost
                  ? currency(item.estimated_cost)
                  : "No price"}
                <br />
                <small>
                  {item.property} ·{" "}
                  {item.eligible
                    ? "Available"
                    : "Already estimated, imported, resolved, or missing cost"}
                </small>
              </span>
            </label>
          ))}
          {query.data?.length === 0 && (
            <Typography>No Maintenance findings for this customer.</Typography>
          )}
          <Button
            loading={pending}
            disabled={!selected.length}
            onClick={async () => {
              setPending(true);
              setError("");
              try {
                await proposalDealsApi.action(deal.id, "import-maintenance", {
                  item_ids: selected,
                  revision: deal.revision,
                });
                setSelected([]);
                await refresh();
                await query.refetch();
              } catch (e) {
                setError(dealError(e));
              } finally {
                setPending(false);
              }
            }}
          >
            Import selected findings
          </Button>
          {error && <Alert color="danger">{error}</Alert>}
        </>
      )}
    </Card>
  );
}

function findingComplete(deal: Deal, finding: Finding) {
  if (["not_applicable", "inaccessible"].includes(finding.result)) {
    return (
      finding.allow_exception &&
      !!finding.exception_reason.trim() &&
      !!finding.reviewed_by
    );
  }
  return (
    finding.result === "documented" &&
    !!finding.notes.trim() &&
    deal.evidence.filter((photo) => photo.finding === finding.id).length >=
      finding.minimum_photos
  );
}

function Readiness({ deal }: { deal: Deal }) {
  return (
    <div className="deal-readiness">
      <Typography
        level="title-sm"
        startDecorator={
          deal.readiness.ready ? (
            <CheckCircle size={18} />
          ) : (
            <ClipboardText size={18} />
          )
        }
      >
        {deal.readiness.ready ? "Ready to prepare" : "Before you prepare"}
      </Typography>
      {deal.readiness.ready ? (
        <Typography level="body-sm">
          Required information is complete. Review the customer preview before
          preparing paperwork.
        </Typography>
      ) : (
        <details>
          <summary>
            {deal.readiness.issues.length}{" "}
            {deal.readiness.issues.length === 1 ? "item needs" : "items need"}{" "}
            attention
          </summary>
          <ul>
            {deal.readiness.issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function InspectionWork({
  deal,
  scope,
  refresh,
  onDocuments,
}: {
  deal: Deal;
  scope: string;
  refresh: () => Promise<void>;
  onDocuments: () => void;
}) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected =
    deal.findings.find((f) => f.id === selectedId) ||
    deal.findings.find((f) => !findingComplete(deal, f)) ||
    deal.findings[0];
  const complete = deal.findings.filter((f) => findingComplete(deal, f)).length;
  return (
    <>
      <div className="deal-section-heading">
        <div>
          <Typography level="h4" component="h2">
            Build the inspection record
          </Typography>
          <Typography level="body-sm">
            Work through each area to prepare a clear, photo-backed report.
          </Typography>
        </div>
        <Button
          variant="outlined"
          endDecorator={<ArrowRight />}
          onClick={onDocuments}
        >
          Review paperwork
        </Button>
      </div>
      <div className="deal-inspection-layout">
        <Card className="deal-checklist">
          <div>
            <Typography level="title-md">Inspection checklist</Typography>
            <Typography level="body-sm">
              {complete} of {deal.findings.length} areas complete
            </Typography>
          </div>
          <progress
            aria-label="Inspection areas completed"
            value={complete}
            max={deal.findings.length || 1}
          />
          <div className="deal-checklist-items">
            {deal.findings.map((finding, index) => {
              const done = findingComplete(deal, finding);
              const photos = deal.evidence.filter(
                (p) => p.finding === finding.id,
              ).length;
              return (
                <button
                  key={finding.id}
                  className="deal-checklist-item"
                  aria-pressed={selected?.id === finding.id}
                  onClick={() => setSelectedId(finding.id)}
                >
                  <span
                    className={`deal-step-number ${done ? "is-complete" : ""}`}
                  >
                    {done ? (
                      <CheckCircle size={20} weight="fill" />
                    ) : (
                      String(index + 1).padStart(2, "0")
                    )}
                  </span>
                  <span>
                    <strong>{finding.title}</strong>
                    <small>
                      {done
                        ? "Complete"
                        : finding.result === "inaccessible" ||
                            finding.result === "not_applicable"
                          ? "Exception needs review"
                          : `${photos} of ${finding.minimum_photos} photos · ${finding.result === "documented" ? "Documented" : "In progress"}`}
                    </small>
                  </span>
                </button>
              );
            })}
          </div>
          <Readiness deal={deal} />
        </Card>
        {selected ? (
          <FindingEditor
            key={selected.id}
            deal={deal}
            finding={selected}
            scope={scope}
            refresh={refresh}
          />
        ) : (
          <Card>
            <Typography>No inspection areas configured.</Typography>
          </Card>
        )}
      </div>
    </>
  );
}

function RepairWork({
  deal,
  scope,
  refresh,
  onDocuments,
  onDetails,
  onRevise,
  pending,
}: {
  deal: Deal;
  scope: string;
  refresh: () => Promise<void>;
  onDocuments: () => void;
  onDetails: () => void;
  onRevise: () => void;
  pending: boolean;
}) {
  return (
    <>
      <div className="deal-section-heading">
        <div>
          <Typography level="h4" component="h2">
            Define exactly what gets repaired
          </Typography>
          <Typography level="body-sm">
            A clear scope, an agreed price, and one repair authorization.
          </Typography>
        </div>
        <Chip variant="soft">
          {deal.repair_items.length} scope{" "}
          {deal.repair_items.length === 1 ? "item" : "items"}
        </Chip>
      </div>
      {deal.scope_locked && (
        <Alert
          endDecorator={
            <Button loading={pending} onClick={onRevise}>
              Edit proposal
            </Button>
          }
        >
          This proposal has been shared, authorized, linked to a job, or revised.
          Create an editable revision to change its items, pricing, or discounts.
          Signed versions stay preserved.
        </Alert>
      )}
      <div className="deal-repair-layout">
        <Stack spacing={2}>
          {deal.repair_items.map((item, index) => (
            <details className="deal-scope-item" key={item.id}>
              <summary>
                <span className="deal-step-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="deal-scope-description">
                  <strong>{item.title}</strong>
                  <small>
                    {item.location} · {Number(item.quantity)} {item.unit} ×{" "}
                    {currency(item.unit_price)}
                  </small>
                  <span>{item.solution}</span>
                  {item.source_item && (
                    <small className="deal-source-label">
                      From Maintenance
                    </small>
                  )}
                </span>
                <span className="deal-scope-price">
                  <strong>
                    {currency(
                      item.line_total ??
                        String(Number(item.quantity) * Number(item.unit_price)),
                    )}
                  </strong>
                  <small>
                    {deal.scope_locked ? "View details" : "Edit details"}
                  </small>
                </span>
              </summary>
              <RepairEditor
                deal={deal}
                item={item}
                scope={scope}
                refresh={refresh}
              />
            </details>
          ))}
          {!deal.scope_locked && (
            <RepairTaskLibrary deal={deal} refresh={refresh} />
          )}
          {!deal.scope_locked && (
            <details
              className="deal-scope-item deal-add-scope"
              open={!deal.repair_items.length}
            >
              <summary>
                <Plus size={20} />
                <strong>Add a scope item</strong>
                <span className="deal-summary-hint">
                  Describe and price the work
                </span>
              </summary>
              <RepairEditor deal={deal} scope={scope} refresh={refresh} />
            </details>
          )}
          <MaintenanceImport deal={deal} refresh={refresh} />
        </Stack>
        <Card className="deal-price-summary">
          <Typography level="title-md">Repair estimate</Typography>
          <Typography level="body-sm">Based on the current scope</Typography>
          <RepairPriceBreakdown totals={deal.totals} />
          {!deal.scope_locked && (
            <SavedForm
              key={deal.revision}
              cacheKey={`${scope}:discount`}
              revision={deal.revision}
              submit="Save proposal discount"
              onSave={async (v, revision) => {
                await proposalDealsApi.patch(deal.id, { ...v, revision });
                await refresh();
              }}
            >
              <RepairDiscountFields
                type={deal.discount_type}
                value={deal.discount_value}
                label="Proposal discount"
              />
              <Typography level="body-xs">
                Applied after line discounts, before tax. Use 0 for no discount.
              </Typography>
            </SavedForm>
          )}
          <Button onClick={onDocuments} endDecorator={<ArrowRight />}>
            Review authorization
          </Button>
          {!deal.scope_locked && (
            <Button variant="plain" onClick={onDetails}>
              Edit terms & tax
            </Button>
          )}
          {deal.scope_locked ? (
            <Typography level="body-sm">
              Authorization prepared. Follow the next action above to continue.
            </Typography>
          ) : (
            <Readiness deal={deal} />
          )}
        </Card>
      </div>
    </>
  );
}

export default function DealProposalWorkspace() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [tab, setTab] = useState("work"),
    [error, setError] = useState(""),
    [pending, setPending] = useState(false);
  const requestIds = useRef<Record<string, string>>({});
  const query = useQuery({
    queryKey: ["proposal-deal", id, searchParams.get("handoff")],
    queryFn: () => proposalDealsApi.get(Number(id)),
    refetchInterval: (q) =>
      q.state.data?.documents.some((d) => d.state === "pending")
        ? 2000
        : q.state.data?.documents.some((d) =>
              ["sent", "viewed", "in_progress"].includes(
                d.contract_status || "",
              ),
            )
          ? 10000
          : false,
  });
  const settings = useQuery({
    queryKey: ["proposal-deal-settings"],
    queryFn: proposalDealsApi.settings,
  });
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ["proposal-deal", id] });
    await client.invalidateQueries({ queryKey: ["proposal-deals"] });
  };
  async function run(fn: () => Promise<unknown>) {
    setPending(true);
    setError("");
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(dealError(e));
    } finally {
      setPending(false);
    }
  }
  if (query.isPending || settings.isPending)
    return <Typography sx={{ p: 3 }}>Loading proposal workspace…</Typography>;
  if (query.isError || !query.data || settings.isError)
    return (
      <Alert
        color="danger"
        sx={{ m: 3 }}
        endDecorator={
          <Button
            onClick={() =>
              void Promise.all([query.refetch(), settings.refetch()])
            }
          >
            Retry
          </Button>
        }
      >
        This proposal is unavailable.
      </Alert>
    );
  const deal = query.data,
    insurance = deal.deal_type === "insurance",
    scope = `proposal-deal:${settings.data?.current_user}:${deal.id}`;
  const prepareDocument = async (kind: DealDocument["kind"]) => {
    if (
      kind !== "contingency" &&
      Object.keys(localStorage).some(
        (key) =>
          key.startsWith(`${scope}:`) &&
          !key.startsWith(`${scope}:share:`) &&
          key !== `${scope}:job`,
      )
    )
      throw new Error(
        "Save your draft findings, details, or scope before preparing this document.",
      );
    return proposalDealsApi.action<DealDocument>(deal.id, "prepare-document", {
      kind,
      revision: deal.revision,
    });
  };
  const prepare = (kind: DealDocument["kind"]) =>
    run(() => prepareDocument(kind));
  const requestId = (key: string) =>
    requestIds.current[key] || (requestIds.current[key] = crypto.randomUUID());
  return (
    <Box
      className="deal-workspace"
      sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1480, mx: "auto" }}
    >
      <Stack spacing={2}>
        <Button
          component={Link}
          to={`/crm/proposals?deal_type=${deal.deal_type}`}
          variant="plain"
          sx={{ alignSelf: "start", px: 0 }}
          startDecorator={<ArrowLeft size={17} />}
        >
          Back to Proposals
        </Button>
        <div className="deal-heading">
          <div>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
              <Chip
                variant="soft"
                color="primary"
                size="sm"
                startDecorator={
                  insurance ? <ShieldCheck size={15} /> : <Wrench size={15} />
                }
              >
                {insurance ? "Insurance" : "Repair"}
              </Chip>
              <Typography level="body-xs">{deal.proposal_number}</Typography>
            </Stack>
            <Typography
              level="h2"
              sx={{ fontSize: { xs: 25, md: 30 }, letterSpacing: "-0.035em" }}
            >
              {deal.title}
            </Typography>
            <Typography level="body-sm" sx={{ mt: 1 }}>
              {deal.contact_name} <span aria-hidden="true"> · </span>{" "}
              {deal.opportunity_name}
            </Typography>
          </div>
          <Chip
            className="deal-status"
            variant="soft"
            size="sm"
            startDecorator={<span className="deal-status-dot" />}
          >
            {deal.status_label}
          </Chip>
        </div>
        {error && <Alert color="danger">{error}</Alert>}
        {!insurance && (
          <RepairNextStep
            key={deal.id}
            deal={deal}
            pending={pending}
            prepare={() => prepareDocument("repair_authorization")}
            refresh={refresh}
            onTab={setTab}
          />
        )}
        {settings.data &&
          !settings.data[
            insurance ? "insurance_configured" : "repair_configured"
          ] && (
            <Alert variant="soft">
              <span>
                Using default {insurance ? "Insurance" : "Repair"} settings.
                Edit{" "}
                <Link to="/crm/proposals/settings?tab=deal_workflows">
                  Proposal Settings → Insurance &amp; Repair
                </Link>{" "}
                to match your process.
              </span>
            </Alert>
          )}
        {settings.data &&
          !settings.data[
            insurance ? "insurance_enabled" : "repair_enabled"
          ] && (
            <Alert>
              New issuance is disabled. Existing documents and signing links
              remain available.
            </Alert>
          )}
        {deal.source_id && (
          <Typography level="body-sm">
            Revision of{" "}
            <Link to={`/crm/proposals/deals/${deal.source_id}`}>
              earlier proposal
            </Link>
            . Earlier documents remain preserved.
          </Typography>
        )}
        <nav className="deal-nav" aria-label="Proposal workspace">
          {[
            ["work", insurance ? "Inspection" : "Scope & pricing"],
            ["details", insurance ? "Claim & details" : "Terms & details"],
            ["documents", insurance ? "Report & contingency" : "Authorization"],
            ["preview", "Customer preview"],
            ["handoff", "Handoff & activity"],
          ].map(([key, label]) => (
            <button
              key={key}
              aria-pressed={tab === key}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>
        {tab === "work" &&
          (insurance ? (
            <InspectionWork
              key={deal.id}
              deal={deal}
              scope={scope}
              refresh={refresh}
              onDocuments={() => setTab("documents")}
            />
          ) : (
            <RepairWork
              deal={deal}
              scope={scope}
              refresh={refresh}
              onDocuments={() => setTab("documents")}
              onDetails={() => setTab("details")}
              pending={pending}
              onRevise={() => {
                if (
                  !window.confirm(
                    "Create an editable revision? All scope items and photos will be copied. Any unsigned authorization will be cancelled and must be sent again after your changes. Signed documents and existing jobs stay unchanged.",
                  )
                )
                  return;
                void run(async () => {
                  const result = await proposalDealsApi.action<Deal>(
                    deal.id,
                    "revise",
                    {
                      request_id: requestId(`revise:${deal.id}`),
                      supersede_pending: true,
                    },
                  );
                  setTab("work");
                  navigate(`/crm/proposals/deals/${result.id}`);
                });
              }}
            />
          ))}
        {insurance && tab === "work" && (
          <DealSupportingDocuments deal={deal} refresh={refresh} />
        )}
        {tab === "details" && (
          <div className="deal-grid">
            <Card>
              <Typography level="title-lg">Proposal details</Typography>
              <SavedForm
                key={deal.revision}
                cacheKey={`${scope}:details`}
                revision={deal.revision}
                disabled={deal.scope_locked}
                onSave={async (v, revision) => {
                  await proposalDealsApi.patch(deal.id, {
                    ...v,
                    revision,
                    inspection_date: v.inspection_date || null,
                    inspection_owner: deal.inspection_owner,
                    tax_enabled: v.tax_enabled === "yes",
                  });
                  await refresh();
                }}
              >
                <Field label="Title" name="title" value={deal.title} required />
                {insurance ? (
                  <>
                    <Field
                      label="Inspection date"
                      name="inspection_date"
                      value={deal.inspection_date}
                      type="date"
                    />
                    <Typography level="body-sm">
                      Inspector #{deal.inspection_owner} ·{" "}
                      <button
                        type="button"
                        onClick={() =>
                          void run(() =>
                            proposalDealsApi.patch(deal.id, {
                              revision: deal.revision,
                              inspection_owner: settings.data?.current_user,
                            }),
                          )
                        }
                      >
                        Assign to me
                      </button>
                    </Typography>
                  </>
                ) : (
                  <>
                    <label>
                      Tax
                      <select
                        name="tax_enabled"
                        defaultValue={deal.tax_enabled ? "yes" : "no"}
                      >
                        <option value="no">Disabled</option>
                        <option value="yes">Enabled</option>
                      </select>
                    </label>
                    <Field
                      label="Tax rate (%)"
                      name="tax_rate"
                      value={deal.tax_rate}
                      type="number"
                    />
                    <Notes
                      label="Repair terms, payment terms, and warranty"
                      name="terms"
                      value={
                        deal.terms ||
                        (!deal.scope_locked ? settings.data?.repair_terms : "")
                      }
                      required
                    />
                  </>
                )}
                <Notes
                  label={
                    insurance
                      ? "Inspection limitations"
                      : "Work excluded / assumptions"
                  }
                  name="exclusions"
                  value={deal.exclusions}
                />
              </SavedForm>
            </Card>
            {insurance && deal.claim && (
              <Card>
                <Typography level="title-lg">Claim assistance</Typography>
                <Typography level="body-sm">
                  Track filing and carrier responses. These notes stay internal.
                </Typography>
                <SavedForm
                  key={deal.revision}
                  cacheKey={`${scope}:claim`}
                  revision={deal.revision}
                  onSave={async (v, revision) => {
                    await proposalDealsApi.patch(
                      deal.id,
                      {
                        ...v,
                        revision,
                        date_of_loss: v.date_of_loss || null,
                        deductible_amount: v.deductible_amount || null,
                      },
                      "claim/",
                    );
                    await refresh();
                  }}
                >
                  <label>
                    Claim status
                    <select
                      name="status"
                      defaultValue={String(deal.claim.status)}
                    >
                      {[
                        "not_filed",
                        "filed",
                        "adjuster_assigned",
                        "pending",
                        "approved",
                        "partially_approved",
                        "denied",
                        "withdrawn",
                        "follow_up",
                      ].map((s) => (
                        <option key={s} value={s}>
                          {s.replaceAll("_", " ")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="deal-field-pair">
                    {[
                      ["carrier", "Carrier"],
                      ["claim_number", "Claim number"],
                      ["policy_number", "Policy number"],
                      ["adjuster_name", "Adjuster name"],
                      ["adjuster_email", "Adjuster email"],
                      ["adjuster_phone", "Adjuster phone"],
                    ].map(([name, label]) => (
                      <Field
                        key={name}
                        name={name}
                        label={label}
                        value={String(deal.claim?.[name] || "")}
                        type={name === "adjuster_email" ? "email" : "text"}
                      />
                    ))}
                    <Field
                      name="date_of_loss"
                      label="Reported loss date (optional)"
                      type="date"
                      value={String(deal.claim.date_of_loss || "")}
                    />
                    <Field
                      name="deductible_amount"
                      label="Deductible (optional)"
                      type="number"
                      value={String(deal.claim.deductible_amount || "")}
                    />
                  </div>
                  <Notes
                    name="next_action"
                    label="Next action"
                    value={String(deal.claim.next_action || "")}
                  />
                  <Notes
                    name="notes"
                    label="Internal claim notes"
                    value={String(deal.claim.notes || "")}
                  />
                </SavedForm>
                <Typography level="body-sm">
                  Track homeowner filing and carrier responses here. Sharing a
                  report does not file a claim.
                </Typography>
              </Card>
            )}
          </div>
        )}
        {tab === "documents" && (
          <>
            <div className="deal-grid deal-document-actions">
              {insurance && (
                <Card>
                  <span className="deal-feature-icon">
                    <FileText size={24} />
                  </span>
                  <Typography level="h4" component="h2">
                    Inspection report
                  </Typography>
                  <Typography level="body-sm">
                    Share the documented findings and photos with the homeowner
                    and adjuster.
                  </Typography>
                  <Readiness deal={deal} />
                  <Button
                    loading={pending}
                    disabled={!deal.readiness.ready}
                    onClick={() => void prepare("inspection")}
                  >
                    Prepare inspection report
                  </Button>
                </Card>
              )}
              {insurance && (
                <Card>
                  <span className="deal-feature-icon">
                    <ShieldCheck size={24} />
                  </span>
                  <Typography level="h4" component="h2">
                    {settings.data?.contingency_template
                      ? "Contingency agreement"
                      : "Inspection authorization"}
                  </Typography>
                  <Typography level="body-sm">
                    {settings.data?.contingency_template
                      ? "Get the homeowner’s agreement to move forward while the claim is being reviewed."
                      : "Authorize the inspection using the included agreement. Construction scope and pricing are agreed separately."}
                  </Typography>
                  <Typography level="body-sm" sx={{ flex: 1 }}>
                    Available before claim filing or adjuster assignment. Review
                    the agreement and recipients in Contracts before sending.
                  </Typography>
                  <Button
                    loading={pending}
                    variant="outlined"
                    onClick={() => void prepare("contingency")}
                  >
                    {settings.data?.contingency_template
                      ? "Prepare contingency agreement"
                      : "Prepare inspection authorization"}
                  </Button>
                </Card>
              )}
            </div>
            {!!deal.documents.length && (
              <Typography level="title-lg" sx={{ mt: 1 }}>
                Prepared documents
              </Typography>
            )}
            {deal.documents.map((doc) => (
              <Card key={doc.id}>
                <Stack
                  direction="row"
                  gap={1}
                  justifyContent="space-between"
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Typography level="title-md">
                    {
                      {
                        inspection: "Inspection report",
                        contingency: "Contingency agreement",
                        repair_authorization: "Repair authorization",
                        manual_authorization: "Staff authorization record",
                      }[doc.kind]
                    }{" "}
                    · Version {doc.version}
                  </Typography>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={doc.state === "ready" ? "success" : "neutral"}
                  >
                    {(doc.contract_status || doc.state).replaceAll("_", " ")}
                  </Chip>
                </Stack>
                {doc.error && <Alert color="warning">{doc.error}</Alert>}
                {doc.authorization && (
                  <Stack spacing={0.5}>
                    <Typography level="body-sm">
                      Recorded by {doc.authorization.recorded_by_name} ·{" "}
                      {new Date(doc.authorization.recorded_at).toLocaleString()}
                    </Typography>
                    <Typography>{doc.authorization.reason}</Typography>
                  </Stack>
                )}
                <Stack direction="row" gap={1} useFlexGap flexWrap="wrap">
                  {doc.pdf_url && (
                    <Button
                      variant="outlined"
                      onClick={() =>
                        void run(() =>
                          downloadApiBlob(
                            doc.pdf_url!,
                            `${deal.proposal_number}-${doc.kind}.pdf`,
                          ),
                        )
                      }
                    >
                      Download PDF
                    </Button>
                  )}
                  {doc.contract_url && (
                    <Button component={Link} to={doc.contract_url}>
                      Review agreement & recipients
                    </Button>
                  )}
                  {doc.state === "error" && (
                    <Button
                      onClick={() =>
                        void run(() =>
                          proposalDealsApi.action(
                            deal.id,
                            `documents/${doc.id}/retry`,
                          ),
                        )
                      }
                    >
                      Retry paperwork
                    </Button>
                  )}
                </Stack>
                {doc.kind === "inspection" && doc.state === "ready" && (
                  <SavedForm
                    cacheKey={`${scope}:share:${doc.id}`}
                    revision={deal.revision}
                    submit="Create report-only link"
                    onSave={async (v) => {
                      await proposalDealsApi.action(deal.id, "share-report", {
                        document_id: doc.id,
                        audience: v.audience,
                        email: v.email,
                        expires_in_days: Number(v.days),
                        request_id: requestId(
                          `share:${doc.id}:${v.email}:${v.audience}`,
                        ),
                      });
                      await refresh();
                    }}
                  >
                    <Typography level="title-sm">
                      Create a private report link
                    </Typography>
                    <Typography level="body-sm">
                      Choose one recipient. Creating a link does not send an
                      email.
                    </Typography>
                    <div className="deal-field-pair">
                      <label>
                        Recipient role
                        <select name="audience" defaultValue="homeowner">
                          <option value="homeowner">Homeowner</option>
                          <option value="adjuster">Adjuster</option>
                        </select>
                      </label>
                      <Field
                        label="Recipient email"
                        name="email"
                        type="email"
                        required
                      />
                    </div>
                    <Typography level="body-sm">
                      Homeowner: {deal.contact_email || "Not provided"} ·
                      Adjuster:{" "}
                      {String(deal.claim?.adjuster_email || "Not provided")}
                    </Typography>
                    <Field
                      label="Link expires in days"
                      name="days"
                      type="number"
                      value="30"
                      required
                    />
                  </SavedForm>
                )}
              </Card>
            ))}
            {!!deal.grants.length && (
              <Typography level="title-lg">Report recipients</Typography>
            )}
            {deal.grants.map((grant) => (
              <Card key={grant.id}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip size="sm" variant="soft">
                    {grant.audience === "adjuster" ? "Adjuster" : "Homeowner"}
                  </Chip>
                  <Typography
                    level="title-md"
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {grant.email}
                  </Typography>
                </Stack>
                <Typography>
                  Report #{grant.document} ·{" "}
                  {grant.revoked_at ? "Revoked" : grant.delivery_status} ·{" "}
                  {grant.viewed_at ? "Viewed" : "Not viewed"} · Expires{" "}
                  {new Date(grant.expires_at).toLocaleDateString()}
                </Typography>
                <Stack direction="row" gap={1} useFlexGap flexWrap="wrap">
                  <Button
                    variant="outlined"
                    disabled={!!grant.revoked_at}
                    onClick={() =>
                      void run(() => navigator.clipboard.writeText(grant.url))
                    }
                  >
                    Copy report link
                  </Button>
                  <Button
                    disabled={
                      !!grant.revoked_at || grant.delivery_status === "sent"
                    }
                    loading={pending}
                    onClick={() =>
                      void run(() =>
                        proposalDealsApi.action(
                          deal.id,
                          `grants/${grant.id}/send`,
                        ),
                      )
                    }
                  >
                    {grant.delivery_status === "failed"
                      ? "Retry email"
                      : "Send report email"}
                  </Button>
                  <Button
                    variant="plain"
                    disabled={!!grant.revoked_at}
                    onClick={() =>
                      void run(() =>
                        proposalDealsApi.action(
                          deal.id,
                          `grants/${grant.id}/revoke`,
                        ),
                      )
                    }
                  >
                    Revoke link
                  </Button>
                </Stack>
              </Card>
            ))}
          </>
        )}
        {tab === "preview" && <DealProposalPreview deal={deal} />}
        {tab === "handoff" && (
          <>
            {insurance && (
              <Card>
                <Typography level="title-lg">Next step</Typography>
                <Stack direction="row" gap={1} useFlexGap flexWrap="wrap">
                  {insurance && (
                    <Button
                      loading={pending}
                      onClick={() =>
                        void run(async () => {
                          const result = await proposalDealsApi.action<{
                            id: number;
                          }>(deal.id, "construction-proposal");
                          navigate(`/crm/proposals/${result.id}/edit`);
                        })
                      }
                    >
                      Prepare linked construction proposal
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    loading={pending}
                    onClick={() =>
                      void run(async () => {
                        const result = await proposalDealsApi.action<Deal>(
                          deal.id,
                          "revise",
                          {
                            request_id: requestId(`revise:${deal.id}`),
                            supersede_pending: true,
                          },
                        );
                        navigate(`/crm/proposals/deals/${result.id}`);
                      })
                    }
                  >
                    Create linked revision
                  </Button>
                  <Button
                    component={Link}
                    variant="outlined"
                    to={`/crm/opportunities/${deal.opportunity}`}
                  >
                    Open opportunity for job setup
                  </Button>
                </Stack>
                <Typography level="body-sm">
                  Use the existing opportunity conversion to create the job,
                  then link it here. Creating a linked revision cancels any
                  unsigned agreement. Signed documents and billing records
                  remain preserved.
                </Typography>
                <SavedForm
                  cacheKey={`${scope}:job`}
                  revision={deal.revision}
                  submit="Link job"
                  onSave={async (v) => {
                    await proposalDealsApi.action(deal.id, "link-job", {
                      job: Number(v.job),
                    });
                    await refresh();
                  }}
                >
                  <Field
                    label="Job ID for this opportunity"
                    name="job"
                    type="number"
                    value={deal.job ? String(deal.job) : ""}
                    required
                  />
                </SavedForm>
                {deal.job && (
                  <Link to={`/operations/jobs/${deal.job}`}>
                    Open linked job #{deal.job}
                  </Link>
                )}
              </Card>
            )}
            {!insurance && (
              <Card component="details">
                <Typography component="summary" level="title-md">
                  Need to change the repair scope or terms?
                </Typography>
                <Typography level="body-sm">
                  Create a revision only when something needs changing. This
                  cancels an unsigned agreement; signed documents and existing
                  jobs stay preserved.
                </Typography>
                <Button
                  variant="outlined"
                  loading={pending}
                  sx={{ alignSelf: "start" }}
                  onClick={() =>
                    void run(async () => {
                      const result = await proposalDealsApi.action<Deal>(
                        deal.id,
                        "revise",
                        {
                          request_id: requestId(`revise:${deal.id}`),
                          supersede_pending: true,
                        },
                      );
                      navigate(`/crm/proposals/deals/${result.id}`);
                    })
                  }
                >
                  Create linked revision
                </Button>
              </Card>
            )}
            <Card>
              <Typography level="title-lg">Activity</Typography>
              {!deal.activity.length && (
                <Typography level="body-sm">
                  Saved changes, shared reports, and agreement updates will
                  appear here.
                </Typography>
              )}
              {deal.activity.map((event) => (
                <Typography key={event.id} level="body-sm">
                  {new Date(event.created_at).toLocaleString()} · {event.actor}{" "}
                  · {event.description}
                </Typography>
              ))}
            </Card>
          </>
        )}
      </Stack>
    </Box>
  );
}
